import {
  Component,
  computed,
  EventEmitter,
  input,
  Input,
  OnDestroy,
  OnInit,
  output,
  Output,
  QueryList,
  ViewChildren
} from "@angular/core";
import { compact } from "lodash-es";
import { ReportDesignItemConfig } from "../report-design-item";
import { Subject, Subscription } from "rxjs";
import { debounceTime, map } from "rxjs/operators";
import { isItemEmpty } from "src/app/modules/bootstrap/modules/report/report-support";
import { HealthRecord } from "src/app/backend/models/health-record";

@Component({
  selector: "snt-report-form-renderer",
  templateUrl: "report-form-renderer.component.html",
  styles: [
    `
      :host {
        flex: 1;
      }
    `
  ],
  standalone: false
})
export class ReportFormRendererComponent implements OnInit, OnDestroy {
  @ViewChildren("renderer")
  renderers!: QueryList<any>;

  @Input({ required: true })
  healthRecord: HealthRecord | undefined;

  readonly templateConfig = input<ReportDesignItemConfig[]>([]);
  readonly temlpateConfigWithValues =
    input<Array<ReportDesignItemConfig & { value: string }>>();
  readonly hideEmptyFields = input<boolean>(false);

  @Input()
  dataSource!: Record<string, any>;

  @Input()
  noThrottle: boolean = false;

  @Input()
  throttleTime: number = 500;

  @Input()
  readOnly: boolean = false;

  @Output()
  serializedConfig: EventEmitter<string> = new EventEmitter();

  @Output()
  rawSerializedConfig: EventEmitter<string> = new EventEmitter();

  @Output()
  action: EventEmitter<string> = new EventEmitter();

  @Output()
  custom: EventEmitter<any> = new EventEmitter<void>();

  uploadStarted = output();
  uploadEnded = output();

  private value$: Subject<any> = new Subject<any>();
  private valueSubscription: Subscription | undefined;

  readonly config = computed(() => {
    const base = this.temlpateConfigWithValues() ?? this.templateConfig() ?? [];

    if (!this.hideEmptyFields()) return base;

    return this.#applyHideEmptyFieldsFilter(base);
  });

  stickyTemplates?: ReportDesignItemConfig[];
  templates?: ReportDesignItemConfig[];

  #applyHideEmptyFieldsFilter(
    items: ReportDesignItemConfig[]
  ): ReportDesignItemConfig[] {
    const cloned = JSON.parse(
      JSON.stringify(items)
    ) as ReportDesignItemConfig[];

    return cloned.filter((item) => {
      switch (item.type) {
        case "image":
        case "text":
        case "textarea": {
          return !(item.config.value || (<any>item).value);
        }
        case "tab": {
          const tabs =
            (item.config as { tabs?: { items: ReportDesignItemConfig[] }[] })
              .tabs ?? [];
          const filteredTabs = tabs
            .map((tab) => ({
              ...tab,
              items: tab.items?.filter((subItem) => !isItemEmpty(subItem)) ?? []
            }))
            .filter((tab) => (tab.items?.length ?? 0) > 0);

          (item.config as { tabs: unknown }).tabs = filteredTabs;

          return filteredTabs.length > 0;
        }
        default: {
          return false;
        }
      }
    });
  }

  ngOnInit(): void {
    this.valueSubscription = this.value$
      .pipe(
        map((value) => {
          const serializedValue = JSON.stringify(value);

          this.rawSerializedConfig.emit(serializedValue);

          return serializedValue;
        }),
        this.noThrottle
          ? map((value) => value)
          : debounceTime(this.throttleTime)
      )
      .subscribe((value) => {
        this.serializedConfig.emit(value);
      });
  }

  ngOnDestroy(): void {
    this.valueSubscription?.unsubscribe();
  }

  async generateJsonPdfDataSource(level: number): Promise<string> {
    const results = compact(
      await Promise.all(
        this.renderers
          .toArray()
          .filter((x) => x.level === level)
          .map((x) =>
            !!x.renderPdfObject ? x.renderPdfObject() : Promise.resolve(null)
          )
      )
    );

    return JSON.stringify(results);
  }

  async onValue(): Promise<void> {
    this.value$.next(this.config());
  }
}
